<template>
  <div>
    <v-expansion-panels class="mb-5" v-if="errors.length">
      <v-expansion-panel>
        <v-expansion-panel-header color="#FF8A80" disable-icon-rotate>
          {{ $t("consumption-accounting.errors-found") }} ({{ errors.length }})
          <template v-slot:actions>
            <v-icon color="error">mdi-alert-circle</v-icon>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <FeedbackTable :items="errors"></FeedbackTable>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-form ref="form" class="table-container">
      <v-overlay v-if="loading" absolute z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <table border="1" class="table" ref="table" v-if="list && list.length">
        <thead class="table-head">
          <tr class="remove-on-copy">
            <th
              :colspan="3 + !consumptionPeriodFromContract"
              class="remove-border placeholder-cell"
            ></th>
            <th class="remove-border"></th>
            <th v-if="!showAll" class="remove-on-copy">
              <v-btn
                text
                v-on:click="index > 0 ? index-- : (index = 0)"
                color="primary"
                :disabled="index == 0"
                class="arrows"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </th>
            <th class="show-all-cell">
              <v-btn
                class="arrows"
                text
                color="primary"
                v-on:click="showAll = !showAll"
              >
                <v-icon v-if="!showAll">mdi-arrow-expand-horizontal</v-icon>
                <v-icon v-if="showAll">mdi-arrow-collapse-horizontal</v-icon>
              </v-btn>
            </th>
            <th v-if="!showAll" class="remove-on-copy">
              <v-btn
                class="arrows"
                text
                v-on:click="
                  index < realAxisY.length - 1
                    ? index++
                    : (index = realAxisY.length - 1)
                "
                color="primary"
                :disabled="index == realAxisY.length - 1"
              >
                <v-icon>mdi-arrow-right</v-icon>
              </v-btn>
            </th>
            <th
              class="spec-pagination"
              v-for="n in AxisY.length - isConsumptionReceipt"
              :key="`spec-pagination=${n}`"
            ></th>
          </tr>
          <tr v-for="(key, i) in AxisYParams" v-bind:key="key">
            <th
              v-if="i === 0"
              :rowspan="AxisYParams.length + showTotal"
              :colspan="3 + !consumptionPeriodFromContract"
              class="remove-border placeholder-cell"
            ></th>
            <th class="table-map">{{ $t(`consumption-accounting.${key}`) }}</th>
            <th v-if="!showAll" class="remove-on-copy">
              <v-icon v-if="i === 2 && showTotal && index > 0">
                mdi-dots-horizontal
              </v-icon>
            </th>
            <th
              v-for="(item, y) in AxisY"
              :class="{ [`table-head_${key}`]: true }"
              v-bind:key="item[key] + '-' + y + '-' + key"
            >
              <div>
                <p>{{ item[key] }}</p>
              </div>
            </th>
            <th v-if="!showAll" class="remove-on-copy">
              <v-icon
                v-if="i === 2 && showTotal && index < realAxisY.length - 1"
              >
                mdi-dots-horizontal
              </v-icon>
            </th>
            <th
              v-if="i === 0 && showTotal"
              class="table-head_total"
              :rowspan="AxisYParams.length"
            >
              <div>
                <p>{{ $t("consumption-accounting.total") }}</p>
              </div>
            </th>
          </tr>

          <tr v-if="showTotal">
            <th class="table-map">{{ $t(`consumption-accounting.total`) }}</th>
            <th v-if="!showAll" class="remove-on-copy">
              <v-icon v-if="index > 0">mdi-dots-horizontal</v-icon>
            </th>

            <th
              v-for="(item, y) in AxisY"
              v-bind:key="item['total'] + '-' + y + '-' + 'total'"
              сlass="table-cell-value"
            >
              <div>
                <p>
                  {{ getColumnTotal(item) }}
                </p>
              </div>
            </th>
            <th v-if="!showAll" class="remove-on-copy">
              <v-icon v-if="index < realAxisY.length - 1">
                mdi-dots-horizontal
              </v-icon>
            </th>

            <th>
              <div>
                <p>
                  {{ grossTotal }}
                </p>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th class="table-map table-cell-checkbox">
              <v-checkbox
                :indeterminate="mainCheckedStatus.indeterminate"
                :input-value="mainCheckedStatus.value"
                @click.native="changesMainCheckedStatus"
                color="secondary"
              ></v-checkbox>
            </th>
            <template v-if="!consumptionPeriodFromContract">
              <th class="table-map">
                {{ $t(`consumption-accounting.period`) }}
              </th>
              <th class="table-map table-map_contact">
                {{ $t(`consumption-accounting.contact`) }}
              </th>
            </template>
            <template v-else>
              <th class="table-map table-map_contact">
                {{ $t(`consumption-accounting.contract`) }}
              </th>
            </template>
            <th class="table-map">
              {{ $t(`consumption-accounting.cost-centre`) }}
            </th>
            <th v-if="!showAll" class="remove-on-copy"></th>
            <th
              v-for="(item, u) in AxisY.length + 1 + showTotal"
              v-bind:key="`white-space-${u}`"
            ></th>
            <th v-if="!showAll" class="remove-on-copy"></th>
          </tr>
          <template v-for="item in AxisX">
            <tr
              v-for="(consumer, u) in list.filter(
                i => (i._consumer && i._consumer.id) === item.id
              )"
              v-bind:key="u + '-' + item.id"
            >
              <td class="table-cell-checkbox">
                <v-checkbox
                  color="secondary"
                  v-model="checkedStore[getConsumerIndex(consumer)]"
                ></v-checkbox>
              </td>
              <template v-if="!consumptionPeriodFromContract">
                <td>
                  <div class="d-flex">
                    <DateInput
                      :date="
                        (periodContactStore[getConsumerIndex(consumer)] || {})
                          .period_from
                      "
                      @update="
                        val => updateDateInput(consumer, 'period_from', val)
                      "
                      :label="$t('consumption-accounting.period-from')"
                      clearable
                      :min="billingPeriod.start_date"
                      :max="billingPeriod.end_date"
                      :rules="[rules.requiredForDate]"
                    ></DateInput>
                    <DateInput
                      :date="
                        (periodContactStore[getConsumerIndex(consumer)] || {})
                          .period_to
                      "
                      @update="
                        val => updateDateInput(consumer, 'period_to', val)
                      "
                      :label="$t('consumption-accounting.period-to')"
                      clearable
                      :min="billingPeriod.start_date"
                      :max="billingPeriod.end_date"
                      :rules="[rules.requiredForDate]"
                    ></DateInput>
                  </div>
                </td>
                <td>
                  <AutocompleteContacts
                    :item="
                      (periodContactStore[getConsumerIndex(consumer)] || {})
                        .contact
                    "
                    :updated="
                      val => {
                        (
                          periodContactStore[getConsumerIndex(consumer)] || {}
                        ).contact = val;
                      }
                    "
                    :rules="
                      checkedStore[getConsumerIndex(consumer)]
                        ? [rules.required]
                        : []
                    "
                    label="consumption-accounting.contact"
                  ></AutocompleteContacts>
                </td>
              </template>
              <template v-else>
                <td>
                  {{ generateContractNameFromConsumer(consumer) }}
                  <br />
                  {{ generatePeriodFromConsumer(consumer) }}
                </td>
              </template>
              <td :rowspan="item.size" v-if="u === 0">{{ item.name }}</td>
              <td></td>
              <td v-if="!showAll" class="remove-on-copy"></td>

              <td
                v-for="(itemY, i) in AxisY"
                v-bind:key="
                  `value-${i}-${itemY.id}-consumer-${
                    (consumer._consumer || { id: 'no-id' }).id
                  }`
                "
                сlass="table-cell-value"
                v-html="getValueFromConsumer(consumer, itemY)"
              ></td>
              <td v-if="!showAll" class="remove-on-copy"></td>

              <td v-if="showTotal" class="table-cell-value">
                <p class="value-from-consumer">{{ getRowTotal(consumer) }}</p>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </v-form>
  </div>
</template>

<script>
import Contact from "../../../../store/modules/contactStore";
import AutocompleteContacts from "../../../common/inputs/AutocompleteContacts";
import DateInput from "../../../common/inputs/DateInput";
import { rules } from "../../../mixins/rules";
import copy from "copy-to-clipboard";
import FeedbackTable from "../../../common/feedback/FeedbackTable.vue";

export default {
  name: "PreRenderConsumptionAccounting",
  mixins: [rules],
  components: {
    FeedbackTable,
    AutocompleteContacts,
    DateInput
  },
  props: {
    type: {
      type: Object,
      default: function() {
        return {};
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    consumptionPeriodFromContract: {
      type: Boolean,
      default: false
    },
    billingPeriod: {
      type: Object
    }
  },
  data: () => ({
    checkedStore: [],
    periodContactStore: [],
    index: 0,
    showAll: false,
    AxisYParams: ["provider", "type", "ratio"]
  }),
  computed: {
    list() {
      return this.$store.getters[
        "projectAccountingStore/consumptionAccountingStore/getList"
      ];
    },
    project: {
      get() {
        return this.$store.getters["projectStore/getItem"];
      }
    },
    errors() {
      return Object.values(
        this.list
          .map(i => i.errors)
          .flat()
          .reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {})
      );
    },
    realAxisY() {
      let columns = this.list
        .map(consumer =>
          (
            consumer.costs_detail ||
            consumer.proportions_detail ||
            consumer.counts
          ).map(provider => {
            return {
              id: provider._provider.id,
              provider: provider._provider.name,
              type: provider.type,
              ratio: (provider.applied_factors || {}).ratio,
              total:
                (provider.costs && provider.costs.subject) ||
                (provider.inherited_proportion &&
                  provider.inherited_proportion.subject) ||
                0
            };
          })
        )
        .flat();

      return Array.from(
        new Set(columns.map(a => a.id + a.provider + a.type + a.ratio))
      ).map(id => {
        return columns
          .filter(a => a.id + a.provider + a.type + a.ratio === id)
          .reduce((ac, item) => ({
            ...item,
            total:
              Number(ac.total) +
              Number(item.total || item.proportions_total || 0)
          }));
      });
    },
    AxisY() {
      let list = this.realAxisY;
      return this.showAll ? list : [list[this.index]];
    },
    AxisX() {
      let counts = {};
      this.list.forEach(function(consumer) {
        counts[consumer._consumer.id] = {
          size: ((counts[consumer._consumer.id] || {}).size || 0) + 1,
          name: consumer._consumer.name,
          id: consumer._consumer.id
        };
      });
      return Object.values(counts).sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    },

    mainCheckedStatus() {
      let allTrue = this.checkedStore.reduce((i, ac) => ac && i, true);
      let haveSomeTrue = this.checkedStore.reduce((i, ac) => ac || i, false);
      return {
        value: allTrue,
        indeterminate: allTrue !== haveSomeTrue
      };
    },
    items() {
      let consumptionPeriodFromContract = this.consumptionPeriodFromContract;
      let list = this.list;
      let periodContactStore = this.periodContactStore;
      let res = this.checkedStore.reduce((ac, item, index) => {
        if (item) {
          ac.push(
            consumptionPeriodFromContract
              ? {
                  cost_centre: (list[index]._consumer || {}).id,
                  contract_period: (list[index]._contract_period || {}).id
                }
              : {
                  cost_centre: (list[index]._consumer || {}).id,
                  consumption_period: {
                    start_date: periodContactStore[index].period_from,
                    end_date: periodContactStore[index].period_to
                  },
                  receiver: periodContactStore[index].contact
                }
          );
        }
        return ac;
      }, []);
      return res;
    },
    isConsumptionBill() {
      return this.type.id === "consumption-bill";
    },
    isProportionBill() {
      return this.type.id === "proportions-bill";
    },
    isConsumptionReceipt() {
      return this.type.id === "consumption-receipt";
    },
    showTotal() {
      return this.isConsumptionBill || this.isProportionBill;
    },
    grossTotal() {
      let value = this.realAxisY.reduce((a, b) => a + Number(b.total), 0);
      if (this.isConsumptionBill) {
        return this.$number(value, "currencyValue", "", {
          useCurrency: this.project.currency
        });
      }
      if (this.isProportionBill) {
        return this.proportionAsPermille(value);
      }
      return value;
    }
  },
  methods: {
    prepareContact: Contact.prepareContact,
    getRowTotal(consumer) {
      if (this.isConsumptionBill) {
        return this.$number(consumer.costs_total, "currencyValue", "", {
          useCurrency: this.project.currency
        });
      }
      if (this.isProportionBill) {
        return this.proportionAsPermille(consumer.proportions_total);
      }
      return undefined;
    },
    getColumnTotal(column) {
      if (this.isConsumptionBill) {
        return this.$number(column["total"], "currencyValue", "", {
          cleanCurrency: true
        });
      }
      if (this.isProportionBill) {
        return this.proportionAsPermille(column["total"]);
      }
      return undefined;
    },
    generateContractNameFromConsumer(consumer) {
      if (consumer._contract_period) {
        let contact = this.prepareContact(consumer._contract_period._contact);
        return contact.compiledName;
      }
    },
    generatePeriodFromConsumer(consumer) {
      if (consumer._contract_period) {
        return `(${
          consumer._contract_period.start_date
            ? this.$dateTime(
                new Date(consumer._contract_period.start_date),
                "short"
              )
            : "…"
        }
         - ${
           consumer._contract_period.end_date
             ? this.$dateTime(
                 new Date(consumer._contract_period.end_date),
                 "short"
               )
             : "…"
         })`;
      }
    },
    getValueFromConsumer(consumer, item) {
      let items = (
        consumer.costs_detail ||
        consumer.proportions_detail ||
        consumer.counts
      ).filter(
        i =>
          i._provider &&
          i._provider.id === item.id &&
          item.type === i.type &&
          item.ratio === (i.applied_factors && i.applied_factors.ratio)
      );

      let objectKeys = items.reduce((ac, i) => {
        if (!i) {
          return ac;
        }
        let suffix = "";
        if (this.isProportionBill) {
          suffix =
            i.type === "unitbased"
              ? i.absolute_proportion && i.absolute_proportion.unit
              : "‰";
        }
        if (this.isConsumptionReceipt) {
          suffix = i._meter_unit._ingestion && i._meter_unit._ingestion.symbol;
        }

        let prefix = (i._tariff && i._tariff.name) || "";
        prefix = prefix && prefix + ":";
        ac[`${prefix}_${suffix}`] = ac[`${prefix}_${suffix}`] || {
          prefix,
          suffix,
          value: 0
        };
        ac[`${prefix}_${suffix}`].value =
          ac[`${prefix}_${suffix}`].value +
          +(
            (i.costs && i.costs.subject) ||
            (i.inherited_proportion && i.inherited_proportion.subject * 1000) ||
            i.difference ||
            (i.absolute_proportion && i.absolute_proportion.subject) ||
            0
          );
        return ac;
      }, {});
      let objectValues = [...Object.values(objectKeys)].reduce((ac, i) => {
        let numberFormat = "currencyValue";
        if (this.isConsumptionReceipt) {
          numberFormat = "meterValue";
        }
        if (this.isProportionBill) {
          numberFormat = "proportionalValue";
        }
        ac.push(
          `<p class="value-from-consumer"><b>${i.prefix}</b> ${this.$number(
            i.value,
            numberFormat,
            "",
            {
              cleanCurrency: true
            }
          )}  ${i.suffix}</p>`
        );
        return ac;
      }, []);
      return objectValues.join(``);
    },
    getConsumerIndex(consumer) {
      return this.list.indexOf(consumer);
    },
    changesMainCheckedStatus() {
      this.checkedStore = this.checkedStore.map(
        () => !this.mainCheckedStatus.value
      );
    },
    formParticle(i) {
      let params = {
        billing_period: this.billingPeriod.id,
        project_id: this.$route.params.project_id,
        cost_centres: this.list[i]._consumer.id,
        index: i
      };
      if (
        this.periodContactStore[i] &&
        this.periodContactStore[i].period_from &&
        this.periodContactStore[i].period_to
      ) {
        params.consumption_period = `${this.periodContactStore[i].period_from}/${this.periodContactStore[i].period_to}`;
        let dispatch = "";
        if (this.isConsumptionReceipt) {
          dispatch =
            "projectAccountingStore/consumptionAccountingStore/getCountsListParticle";
        }
        if (this.isConsumptionBill) {
          dispatch =
            "projectAccountingStore/consumptionAccountingStore/getConsumptionListParticle";
        }
        if (this.isProportionBill) {
          dispatch =
            "projectAccountingStore/consumptionAccountingStore/getProportionsListParticle";
        }
        dispatch &&
          this.$store
            .dispatch(dispatch, {
              params,
              onSuccess: () => {}
            })
            .finally(() => (this.tableLoading = false));
      }
    },
    validate() {
      return this.$refs.form.validate();
    },
    setPeriodContactStore() {
      this.checkedStore.length !== this.list.length &&
        (this.checkedStore = this.list.map(() => true));
      this.periodContactStore.length !== this.list.length &&
        (this.periodContactStore = this.list.map(() => ({
          period_from: this.billingPeriod.start_date,
          period_to: this.billingPeriod.end_date,
          contact: ""
        })));
    },
    updateDateInput(consumer, key, val) {
      if (
        (this.periodContactStore[this.getConsumerIndex(consumer)] || {})[
          key
        ] !== val
      ) {
        (this.periodContactStore[this.getConsumerIndex(consumer)] || {})[
          key
        ] = val;
        this.formParticle(this.getConsumerIndex(consumer));
      }
    },
    createTableTextForCopy() {
      let originTable = this.$refs.table;
      let table = document.createElement("table");
      table.innerHTML = this.$refs.table.innerHTML;

      //Remove helper inputs
      table
        .querySelectorAll(".inputWithMask , .remove-on-copy")
        .forEach(a => a.remove());
      //Remove other inputs
      table.querySelectorAll(".v-input").forEach(a => {
        let input = a.querySelector("input");
        input = originTable.querySelector("#" + input.id);
        if (input) {
          a.outerHTML = input.value;
        }
      });
      //Remove trash attr
      let dataAttr = Object.values(this.$refs.table.attributes)
        .map(i => i.name)
        .find(i => i.startsWith("data-v"));
      table.querySelectorAll(`[${dataAttr}],[class],[id]`).forEach(a => {
        a.removeAttribute(dataAttr);
        a.removeAttribute("id");
      });

      //Remove second class from table-map_contact because excel has bug if see 2 classes
      let tableMapContact = table.querySelector(".table-map_contact");
      tableMapContact && tableMapContact.setAttribute("class", "table-map");
      //Remove checkbox
      let cellWithCheckbox = table.querySelectorAll(".table-cell-checkbox");
      if (cellWithCheckbox.length) {
        cellWithCheckbox.forEach(node => node.remove());
        let placeholderCell = table.querySelector(".placeholder-cell");
        placeholderCell.setAttribute(
          "colspan",
          Number(placeholderCell.getAttribute("colspan")) - 1
        );
      }
      // Remove value-from-consumer becose it form wrong count of cells
      table
        .querySelectorAll(".value-from-consumer + .value-from-consumer")
        .forEach(n => (n.outerHTML = "<br>" + n.innerHTML));
      table
        .querySelectorAll(".value-from-consumer")
        .forEach(n => (n.outerHTML = n.innerHTML));
      let styleForNumber = `style='mso-number-format: "0.00"'`;
      return table.outerHTML
        .replace(/&nbsp;/g, " ")
        .replace(/’/g, "")
        .replace(/<b><\/b>/g, "")
        .replace(/сlass="table-cell-value"/g, styleForNumber);
    },
    createTemplateForCopy() {
      return `
          <html xmlns="http://www.w3.org/1999/xhtml" xml:lang="en" lang="en">
           <head>
           <title>Demo</title>
           <style>
           .table-map {
            background: rgb(229, 229, 229);
          }
          </style>
           </head><body>${this.createTableTextForCopy()}</body>
           </html>
          `;
    },
    copyToClipboard() {
      if (!this.$refs.table || !this.$refs.table.innerHTML) {
        return;
      }
      copy(this.createTemplateForCopy());
      this.$store.dispatch(
        "notificationStore/createSuccessNotificationForApiRequest",
        {
          text: this.$t(`message.success-copy-to-clipboard`)
        }
      );
    },
    proportionAsPermille(value) {
      return this.$number(value * 1000, "proportionalValue") + "‰";
    }
  },
  watch: {
    list() {
      this.setPeriodContactStore();
    },
    billingPeriod() {
      this.setPeriodContactStore();
    },
    items() {
      this.$emit("updateItems", this.items);
    }
  }
};
</script>

<style lang="scss" scoped>
.table {
  border-spacing: 0;
  border-color: #c1c1c1;
  border-style: solid;
  border-left: 0;
  border-top: 0;
  .show-all-cell {
    white-space: nowrap;
    padding: 0 10px;
  }
  .spec-pagination {
    border-top: 0;
    position: relative;
    & + .spec-pagination {
      border: 0;
    }
    &:before {
      content: "";
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      background: #fff;
      left: 1px;
    }
  }
  p {
    margin: 0;
  }
  th,
  td {
    padding: 5px;
    border-color: #c1c1c1;
    border-style: solid;
    border-bottom: 0;
    border-right: 0;
    min-width: 4ex;
    font-size: 14px;
  }
  .d-flex {
    width: 280px;
  }
  &::v-deep p.value-from-consumer {
    white-space: nowrap;
    margin: 0;
    text-align: right;
  }
}
.table-head div {
  display: flex;
  align-items: center;
  justify-content: center;
}
.table-head_provider p,
.table-head_total p {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  margin: auto;
  transform: rotate(180deg);
}
.table-head_type p {
  font-size: 0;
}
.table-head_type p::first-letter {
  font-size: 16px;
}
.table-map {
  background: rgb(229, 229, 229);
}
.table-map_contact {
  min-width: 160px;
}
.table-cell-checkbox {
  width: 40px;
  height: 40px;
  &::v-deep {
    .v-input--selection-controls__input {
      margin: 0;
    }
    .v-input--selection-controls__ripple {
      margin: 0;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
    .v-input__control {
      margin: auto;
    }
    .v-messages {
      display: none;
    }
    .v-input--checkbox,
    .v-input__slot {
      margin: 0;
    }
    .date-input {
      margin: 0;
    }
  }
}
.remove-border {
  border: 0;
}
.table-container {
  width: 100%;
  overflow: auto;
  position: relative;
  min-height: 500px;
}
.error-content {
  position: relative;
  p {
    margin: 0;
  }
}
.table tbody tr:nth-child(2n + 3) {
  background: rgb(229, 229, 229, 55%);
}
.arrows:before {
  background: rgba(0, 0, 0, 0.87);
}
.arrows::v-deep .v-ripple__container {
  color: rgba(0, 0, 0, 0.87);
}
</style>
